'use strict';

define('vb/extensions/dynamic/private/helpers/dataDescriptionMetadataProviderHelper',[
  'vb/extensions/dynamic/private/helpers/configurableMetadataProviderHelper',
  'vb/extensions/dynamic/private/models/layoutResource',
  'vb/extensions/dynamic/private/models/dataDescription',
  'vb/extensions/dynamic/private/models/metadataRules',
  'vb/private/stateManagement/layout',
  'vb/private/constants',
  'vb/private/utils',
], (
  ConfigurableMetadataProviderHelper,
  LayoutResource,
  DataDescription,
  MetadataRules,
  Layout,
  Constants,
  Utils,
) => {
  /** @typedef {import('vb/private/stateManagement/container').default} Container */

  const RESOURCE_DESCRIPTORS = [{
    resourceName: 'layout',
    propertyMap: {
      descriptor: 'data',
      template: 'template',
      viewModel: 'dataModel',
    },
    modelClass: Layout,
    defaultBundleResourceName: Constants.DefaultBundleResourceNames.DYNAMIC_LAYOUTS,
  }, {
    resourceName: 'data-description',
    propertyMap: {
      descriptor: 'clientMetadata',
      viewModel: 'clientMetadataModel',
    },
    modelClass: DataDescription,
    defaultBundleResourceName: Constants.DefaultBundleResourceNames.DYNAMIC_LAYOUTS,
    skipTemplate: true,
  }, {
    resourceName: 'metadata-rules',
    propertyMap: {
      descriptor: 'metadataRules',
      viewModel: 'metadataRulesModel',
    },
    modelClass: MetadataRules,
    defaultBundleResourceName: Constants.DefaultBundleResourceNames.METADATA_RULES,
    skipFunctions: true,
    skipTemplate: true,
  }, {
    resourceName: 'context',
    propertyMap: {
      descriptor: 'contextMetadata',
    },
    modelClass: LayoutResource,
    defaultBundleResourceName: Constants.DefaultBundleResourceNames.DYNAMIC_LAYOUTS,
    skipFunctions: true,
    skipTemplate: true,
  }];

  /**
   * DataDescriptionMetadataProviderHelper can be used as the "vbHelper" interface that is passed to
   * JET dynamic UI metadata provider for interfacing with VB.
   *
   * This extends ConfigurableMetadataProviderHelper, and implements the "data-description.json" version of
   * the helper; the metadata comes from the local JSON file, rather than service openapi3.
   *
   * @see ServiceMetadataProviderHelper for the closely-related openapi3 version.
   *
   * its 'private', but shared with JET; not for use my application developers
   *
   * @extends ConfigurableMetadataProviderHelper
   */

  class DataDescriptionMetadataProviderHelper extends ConfigurableMetadataProviderHelper {
    /**
     * Retrieves an instance of DataDescriptionMetadataProviderHelper.
     *
     * @static
     * @async
     * @param {object} options - Configuration options for the helper.
     * @param {string} options.id - Unique identifier for the helper.
     * @param {string} options.endpoint - Endpoint.
     * @param {*} vbAppContext - Context created by Visual Builder, passed as 'options.context' to components/providers.
     * @param {Container} container - Visual Builder container where the dynamic component with this layout will be used.
     * @returns {Promise<DataDescriptionMetadataProviderHelper>} A promise resolving to the initialized
     *                                                           DataDescriptionMetadataProviderHelper instance.
     */
    static get(options, vbAppContext, container) {
      const helperOptions = DataDescriptionMetadataProviderHelper.getHelperConfiguration();

      const opts = Object.assign({}, options, { helper: helperOptions });

      return new DataDescriptionMetadataProviderHelper(opts, vbAppContext, container).init();
    }

    /**
     * @param options
     * @param vbAppContext
     * @param {Container} container - Visual Builder container where the dynamic component with this layout will be used.
     */
    constructor(options, vbAppContext, container) {
      super(options, vbAppContext, container);
      if (options.path) {
        this.setLayoutRoot(options.path);
      }
    }

    /**
     * the configuration for the base class
     * @returns {Object} An object containing cloned resource descriptors.
     */
    static getHelperConfiguration() {
      return {
        // @ts-ignore
        descriptors: Utils.cloneObject(RESOURCE_DESCRIPTORS),
      };
    }

    /**
     *
     * @returns {Promise<Object>}
     */
    getMetadata() {
      const descriptors = [{
        resourceName: 'data-description',
        propertyMap: {
          descriptor: 'data',
          viewModel: 'dataModel',
        },
        modelClass: LayoutResource,
        defaultBundleResourceName: Constants.DefaultBundleResourceNames.DYNAMIC_LAYOUTS,
        skipTemplate: true,
      }];

      return this.getLayoutPath()
        .then((pathPrefix) => this.loadLayoutResources(pathPrefix, descriptors));
    }
  }

  // expose for unit tests
  DataDescriptionMetadataProviderHelper.RESOURCE_DESCRIPTORS = RESOURCE_DESCRIPTORS;

  return DataDescriptionMetadataProviderHelper;
});

